import { proxy } from 'valtio';
import { handleError } from '@/utils/helpers';

export interface RecordingDurationStore {
  /** 当前录音时长（秒） */
  currentDuration: number;
  /** 录音时长上限（秒） */
  maxDuration: number;
  /** 是否正在计时 */
  isRunning: boolean;
  /** 定时器引用 */
  timerId: number | null;
  /** 是否已到达上限 */
  isLimitReached: boolean;
  
  // 方法
  /** 开始计时 */
  start: () => void;
  /** 停止计时 */
  stop: () => void;
  /** 重置计时器 */
  reset: () => void;
  /** 增加1秒 */
  tick: () => void;
  /** 设置上限 */
  setMaxDuration: (duration: number) => void;
  /** 更新当前用量 */
  updateCurrentDuration: (duration: number) => void;
  /** 检查是否到达上限 */
  checkLimit: () => boolean;
  /** 格式化时长显示 */
  getFormattedDuration: () => string;
  /** 获取剩余时长（秒） */
  getRemainingDuration: () => number;
  /** 获取使用百分比 */
  getUsagePercentage: () => number;
}

export const RecordingDurationStore: RecordingDurationStore = proxy<RecordingDurationStore>({
  currentDuration: 0,
  maxDuration: 60*60*5, // 默认300 分钟
  isRunning: false,
  timerId: null,
  isLimitReached: false,

  start() {
    if (RecordingDurationStore.isRunning) {
      return;
    }

    RecordingDurationStore.isRunning = true;
    RecordingDurationStore.isLimitReached = false;

    // 使用 setInterval 每秒递增
    RecordingDurationStore.timerId = window.setInterval(() => {
      RecordingDurationStore.tick();
    }, 1000);
  },

  stop() {
    if (!RecordingDurationStore.isRunning) {
      return;
    }

    RecordingDurationStore.isRunning = false;

    if (RecordingDurationStore.timerId !== null) {
      clearInterval(RecordingDurationStore.timerId);
      RecordingDurationStore.timerId = null;
    }
  },

  reset() {
    RecordingDurationStore.stop();
    RecordingDurationStore.currentDuration = 0;
    RecordingDurationStore.isLimitReached = false;
  },

  tick() {
    RecordingDurationStore.currentDuration += 1;

    // 检查是否到达上限
    if (RecordingDurationStore.checkLimit()) {
      RecordingDurationStore.isLimitReached = true;
      RecordingDurationStore.stop();
    }
  },

  setMaxDuration(duration: number) {
    if (duration > 0) {
      RecordingDurationStore.maxDuration = duration;
    }
  },

  updateCurrentDuration(duration: number) {
    if (duration >= 0) {
      RecordingDurationStore.currentDuration = duration;
    }
  },

  checkLimit(): boolean {
    return RecordingDurationStore.currentDuration >= RecordingDurationStore.maxDuration;
  },

  getFormattedDuration() {
    const hours = Math.floor(RecordingDurationStore.currentDuration / 3600);
    const minutes = Math.floor((RecordingDurationStore.currentDuration % 3600) / 60);
    const seconds = RecordingDurationStore.currentDuration % 60;

    if (hours > 0) {
      return `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    }
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  },

  getRemainingDuration() {
    const remaining = RecordingDurationStore.maxDuration - RecordingDurationStore.currentDuration;
    return Math.max(0, remaining);
  },

  getUsagePercentage() {
    if (RecordingDurationStore.maxDuration === 0) {
      return 0;
    }
    return Math.min(100, (RecordingDurationStore.currentDuration / RecordingDurationStore.maxDuration) * 100);
  }
});
